import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Options that make a screen's writes to one list run one at a time, in the
 * order they were made.
 *
 * A field that saves itself on blur, or a swatch clicked right after it, starts
 * a write while the one before is still out. Serialised in a mutation scope,
 * each reads its `updatedAt` from the list when its turn comes (`latestVersion`)
 * — after the write before it has put its answer there with `mergeSaved` —
 * instead of from the render that started it, which would be a 409 about the
 * coordinator's own edit. Fetches already on their way are cancelled, and the
 * refetch is left to the last write of a run: one fired between two writes
 * reads the row before the second lands and would put the older value, and
 * the older `updatedAt`, back.
 */
export function inOrder(qc: QueryClient, scopeId: string, listKey: QueryKey, invalidate: () => void) {
  return {
    scope: { id: scopeId },
    onMutate: () => qc.cancelQueries({ queryKey: listKey }),
    onSettled: () => {
      // The write settling here still counts as pending.
      const pending = qc.isMutating({ predicate: (m) => m.options.scope?.id === scopeId });
      if (pending <= 1) invalidate();
    },
  };
}

/** The `updatedAt` a cached list holds for one row, read at the moment of asking. */
export function latestVersion(qc: QueryClient, listKey: QueryKey, id: string): string | undefined {
  return qc.getQueryData<{ id: string; updatedAt: string }[]>(listKey)?.find((row) => row.id === id)
    ?.updatedAt;
}

/**
 * Writes the fields a save sent, as the server returned them, into the row of a
 * cached list.
 *
 * A form reads its baseline from the list query, and the refetch that follows a
 * save lands a round trip after the toast: until then the screen would say
 * "neshranjene spremembe" about a change the server already holds, and a
 * second save would carry the old `updatedAt` into a 409. Only the sent fields
 * and `updatedAt` are taken, because a PATCH answers with the stored columns
 * while the list carries derived ones the response does not have.
 */
export function mergeSaved<T extends { id: string; updatedAt: string }>(
  qc: QueryClient,
  queryKey: QueryKey,
  id: string,
  saved: object,
  fields: readonly string[],
) {
  const fresh = saved as Record<string, unknown>;
  qc.setQueryData<T[]>(queryKey, (rows) =>
    rows?.map((row) => {
      if (row.id !== id) return row;
      const next: Record<string, unknown> = { ...row, updatedAt: fresh.updatedAt };
      for (const field of fields) if (field in fresh) next[field] = fresh[field];
      return next as T;
    }),
  );
}
